/* eslint-disable react-hooks/refs --
   Always-fresh ref pattern: fetchRef / urlRef are updated every render
   so the mount-only useEffect's setInterval handler can read the
   latest fetch fn + URL without re-establishing the timer. */
/**
 * Lightweight analytics tracking hook.
 *
 * Records the page currently on screen and batches the queue to the
 * backend. The caller (App.jsx) says WHICH page is on screen; this hook owns
 * everything after that — dedup, the queue, and delivery — and knows nothing
 * about react-router.
 *
 * Session ID:
 *   Generated once per browser tab session (sessionStorage).
 *   Sent as X-Session-ID header on all API requests to group events.
 */

import { useEffect, useRef, useCallback } from 'react';

// The session id is owned by utils/sessionId — the transports stamp it as
// X-Session-ID too, and it must not be reachable only through the analytics
// layer. Import it from there, not from this module.
import { getSessionId } from '../utils/sessionId';

// ── Flush logic ─────────────────────────────────────────────────────

const FLUSH_INTERVAL_MS = 30000; // 30 seconds
// Events per request. MUST match MAX_BATCH_SIZE in api/views/analytics.py —
// pinned from the backend by test_client_chunk_size_matches_the_ingest_cap
// (api/test_analytics_store_activity.py).
const MAX_BATCH = 50;
// Ten flushes' worth of backlog. Reached only when the endpoint has been
// unreachable for minutes; past that, holding more costs memory to preserve
// events nobody will read.
const MAX_QUEUE = 500;
const queue = [];
let flushTimer = null;

// Stop firing tracking requests once the backend tells us the user is
// unauthenticated (post-logout): the session is gone, so there is nothing to
// attribute the events to. Reset on the next mount after login.
let authFailedLatch = false;

/**
 * Enqueue a custom event for the next batch flush.
 *
 * For an action no request of ours sees (a command palette search, a link
 * out to Shopify). Events ride the same 30s batch as page_view — no extra
 * network overhead.
 *
 * The server accepts only the types in `_INGEST_CLEANERS`
 * (api/views/analytics.py), each with its declared property keys; anything
 * else comes back in `rejected` and is not stored.
 *
 * @param {string} eventType - A type in `_INGEST_CLEANERS`
 * @param {Object} properties - Exactly the keys that type declares
 */
export function trackEvent(eventType, properties = {}) {
  if (authFailedLatch) return;
  queue.push({ event_type: eventType, properties });
}

function stopFlushing() {
  if (flushTimer) {
    clearInterval(flushTimer);
    flushTimer = null;
  }
  // Discard anything queued — those events would 401 too.
  queue.length = 0;
}

/**
 * Send queued events to backend.
 *
 * @param {Function} fetchFn - The fetch function to use
 * @param {string} apiUrl - The tracking endpoint URL
 */
async function flush(fetchFn, apiUrl) {
  // One request per MAX_BATCH chunk, oldest first, so the server's cap at the
  // same number is a backstop rather than the normal path.
  while (queue.length > 0 && !authFailedLatch) {
    const delivered = await flushChunk(fetchFn, apiUrl, queue.splice(0, MAX_BATCH));
    // A chunk that came back (retryable failure, or over-cap overflow) is on
    // the queue again; stop rather than spin on it until the next tick.
    if (!delivered) return;
  }
}

/**
 * POST one chunk. Returns false when the caller should stop flushing —
 * the chunk is back on the queue, or the session is gone.
 */
async function flushChunk(fetchFn, apiUrl, batch) {
  const sessionId = getSessionId();

  try {
    const res = await fetchFn(apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        session_id: sessionId,
        events: batch,
      }),
    });
    // 401 = session gone (user logged out in another tab, or expired).
    // Latch and stop the interval — retrying forever just pollutes logs.
    if (res && res.status === 401) {
      authFailedLatch = true;
      stopFlushing();
      return false;
    }
    if (res && res.ok) {
      // The server hands back anything past its own cap rather than cutting
      // it. Normally empty — the chunking above keeps us under it.
      const overflow = await readOverflow(res);
      if (overflow.length) {
        requeue(overflow);
        return false;
      }
      return true;
    }

    // A 4xx means the SERVER decided this batch is not storable, and waiting
    // will not change that. It is never re-queued: on 400 `store_required`
    // (no store the user may write to) a later delivery would attribute the
    // events to whichever store the user selected in the meantime.
    const status = res ? res.status : 0;
    if (status >= 400 && status < 500) {
      console.warn(`useTracking: server rejected ${batch.length} event(s) (${status}).`);
      return true;
    }
    // 5xx / no response: ours to retry. The events are already out of the
    // queue, so putting them back is the difference between a delayed page
    // view and a lost one.
    requeue(batch);
    return false;
  } catch {
    // Network failure — same reasoning. The app never sees this either way.
    requeue(batch);
    return false;
  }
}

/**
 * The events the server could not fit, or [] when it fit them all.
 *
 * An unreadable body reads as [] and is logged: the response is a 200, so the
 * chunk was stored, and only an overflow past the server's cap is lost.
 */
async function readOverflow(res) {
  if (typeof res.json !== 'function') return [];
  try {
    const body = await res.json();
    return Array.isArray(body?.overflow) ? body.overflow : [];
  } catch (err) {
    console.warn(
      'useTracking: tracking response body unreadable — assuming no overflow. '
      + 'Any events past the server cap are lost.', err,
    );
    return [];
  }
}

/**
 * Return a failed batch to the front of the queue, oldest-first.
 *
 * Bounded at MAX_QUEUE: past it, the OLDEST events are dropped, and the drop
 * is logged.
 */
function requeue(batch) {
  queue.unshift(...batch);
  if (queue.length > MAX_QUEUE) {
    const dropped = queue.length - MAX_QUEUE;
    queue.splice(0, dropped);
    console.warn(`useTracking: queue full — dropped ${dropped} oldest event(s).`);
  }
}

// ── Hook ────────────────────────────────────────────────────────────

/**
 * Record the page on screen, and keep the queue flowing.
 *
 * @param {Object} options
 * @param {Function} options.fetchFn - Authenticated fetch function
 * @param {string} options.apiUrl - Tracking endpoint URL
 * @param {string|null} options.path - The page currently on screen, already
 *   resolved: a route PATTERN (`/purchase-orders/:id`, so a store
 *   with 400 suppliers produces one row rather than 400) or a guard
 *   screen's own path. Null means "nothing trackable is on screen" and
 *   records nothing — an unknown route, or a shell still deciding what to
 *   render. The caller resolves it from its route table.
 */
export function useTracking({ fetchFn, apiUrl, path = null } = {}) {
  const prevPathRef = useRef(null);
  const fetchRef = useRef(fetchFn);
  const urlRef = useRef(apiUrl);

  // Keep refs in sync
  fetchRef.current = fetchFn;
  urlRef.current = apiUrl;

  const doFlush = useCallback(() => {
    if (fetchRef.current && urlRef.current) {
      flush(fetchRef.current, urlRef.current);
    }
  }, []);

  // Record each page as it comes on screen
  useEffect(() => {
    // Nothing trackable on screen. The raw URL is never recorded instead.
    if (!path) return;

    // Skip duplicate consecutive views (e.g. re-renders). Keyed on the
    // RESOLVED page, so two routes sharing a pattern still record once
    // each, and a guard screen replacing another at the same URL records
    // as the change it is.
    if (path === prevPathRef.current) return;
    prevPathRef.current = path;

    queue.push({ event_type: 'page_view', properties: { path } });
  }, [path]);

  // Periodic flush
  useEffect(() => {
    // Re-arm after a fresh login: the hook remounts when App boots with a
    // new authenticated user, so any stale 401 latch from the previous
    // session should be cleared.
    authFailedLatch = false;
    flushTimer = setInterval(doFlush, FLUSH_INTERVAL_MS);

    // Unmount is not how a browser session usually ends — closing the tab,
    // switching apps on mobile, or following a link away all tear the page
    // down without running an effect cleanup, taking up to FLUSH_INTERVAL_MS
    // of events with them. `pagehide` is the one event that fires in all of
    // those, including the mobile bfcache path `beforeunload` misses, and
    // `visibilitychange` catches the tab-switch that precedes most of them.
    const flushOnLeave = () => {
      if (!authFailedLatch) doFlush();
    };
    const flushOnHide = () => {
      if (document.visibilityState === 'hidden') flushOnLeave();
    };
    window.addEventListener('pagehide', flushOnLeave);
    document.addEventListener('visibilitychange', flushOnHide);

    return () => {
      if (flushTimer) {
        clearInterval(flushTimer);
        flushTimer = null;
      }
      window.removeEventListener('pagehide', flushOnLeave);
      document.removeEventListener('visibilitychange', flushOnHide);
      // Flush remaining events on unmount (unless we've already given up).
      if (!authFailedLatch) doFlush();
    };
  }, [doFlush]);

}
